import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Stack,
  Button,
  Box,
  styled,
  IconButton,
  useTheme,
  Divider,
  Tooltip,
  Alert,
  Collapse
} from "@mui/material"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import CheckIcon from "@mui/icons-material/Check"
import LanguageSelect from "src/components/LanguageSelect"
import { useResponsive } from "src/utils/useResponsive"
import { Language, useFullLanguageList } from "src/utils/useLanguageList"
import CloseIcon from "@mui/icons-material/Close"
import { useLanguages } from "src/providers"
import { updateUserLanguages } from "src/services/api/user"

interface UpdateLanguageDialogProps {
  open: boolean
  isAlreadySetup: boolean
  onClose: () => void
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    maxWidth: "500px",
    width: "100%",
    borderRadius: "16px",
    [theme.breakpoints.down("md")]: {
      borderRadius: 0
    }
  }
}))

const LanguageFieldLabel = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
  fontSize: "14px",
  fontWeight: 600
}))

const StyledInfoIcon = styled(InfoOutlinedIcon)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.primary,
  backgroundColor: (theme.palette.primary as any)[100],
  borderRadius: "50%",
  cursor: "pointer"
}))

const LanguageItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.5)
}))

const CheckIconStyled = styled(CheckIcon)(() => ({
  fontSize: 18,
  color: "#00BCD4" // Cyan color from the image
}))

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.primary
}))

const UpdateLanguageDialog = ({ open, onClose, isAlreadySetup }: UpdateLanguageDialogProps) => {
  const { t } = useTranslation("profile")
  const { palette } = useTheme()
  const { isMobile } = useResponsive()
  const { nativeLanguages: userNativeLanguages, mainLanguages: userMainLanguages, additionalLanguages: userAdditionalLanguages, setUserLanguages } = useLanguages()

  // Local state for language selections (only update context on button click)
  const [nativeLanguages, setNativeLanguages] = useState<Language[]>([])
  const [mainLanguages, setMainLanguages] = useState<Language[]>([])
  const [additionalLanguages, setAdditionalLanguages] = useState<Language[]>([])

  // State for showing info on mobile
  const [showNativeInfo, setShowNativeInfo] = useState(false)
  const [showMainInfo, setShowMainInfo] = useState(false)
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false)

  // State for success modal
  const [showSuccessModal, setShowSuccessModal] = useState(false)

  const fullLanguageList = useFullLanguageList()

  // Initialize local state from context when dialog opens
  React.useEffect(() => {
    if (open) {
      setNativeLanguages(userNativeLanguages)
      setMainLanguages(userMainLanguages)
      setAdditionalLanguages(userAdditionalLanguages)
    }
  }, [open, userNativeLanguages, userMainLanguages, userAdditionalLanguages])

  const handleInfoClick = (type: "native" | "main" | "additional") => {
    if (!isMobile) return // Only handle clicks on mobile

    switch (type) {
      case "native":
        setShowNativeInfo(!showNativeInfo)
        setShowMainInfo(false)
        setShowAdditionalInfo(false)
        break
      case "main":
        setShowMainInfo(!showMainInfo)
        setShowNativeInfo(false)
        setShowAdditionalInfo(false)
        break
      case "additional":
        setShowAdditionalInfo(!showAdditionalInfo)
        setShowNativeInfo(false)
        setShowMainInfo(false)
        break
    }
  }

  const updateContext = () => {
    const languagesToUpdate = {
      nativeLanguages: nativeLanguages.map((l) => l.languageId),
      mainLanguages: mainLanguages.map((l) => l.languageId),
      additionalLanguages: additionalLanguages.map((l) => l.languageId)
    }
    setUserLanguages(languagesToUpdate)
  }

  const handleUpdate = () => {
    const languagesToUpdate = {
      nativeLanguages: nativeLanguages.map((l) => l.languageId),
      mainLanguages: mainLanguages.map((l) => l.languageId),
      additionalLanguages: additionalLanguages.map((l) => l.languageId)
    }

    updateUserLanguages(languagesToUpdate)
      .then(() => {
        if (!isAlreadySetup) {
          setShowSuccessModal(true)
        } else {
          updateContext()
        }
        // Close the update dialog
        onClose()
      })
      .catch((error) => {
        console.error("Failed to update languages:", error)
        // Handle error (could show a snackbar here)
      })
  }

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false)
    updateContext()
  }

  const getLanguageName = (langCode: string): string => {
    return t(`lang:${langCode}`, { defaultValue: langCode })
  }

  const renderLanguageSection = (languages: string[], titleKey: string) => {
    if (languages.length === 0) return null

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          {t(titleKey)}
        </Typography>
        <Stack spacing={0.5}>
          {languages.map((lang) => (
            <LanguageItem key={lang}>
              <CheckIconStyled />
              <Typography variant="body2" sx={{ color: palette.text.primary }}>
                {getLanguageName(lang)}
              </Typography>
            </LanguageItem>
          ))}
        </Stack>
      </Box>
    )
  }

  return (
    <>
      <StyledDialog open={open} onClose={onClose} fullScreen={isMobile}>
        <DialogTitle
          sx={{ p: 3, mb: 2, gap: 1, display: "flex", flexDirection: "column", backgroundColor: palette.primary.light }}
        >
          <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ flex: 1, flexGrow: 0 }}>
            <IconButton
              onClick={onClose}
              sx={{
                color: "text.primary"
              }}
            >
              <CloseIcon fontSize="large" />
            </IconButton>
          </Stack>
          <Typography variant="h5" component="div" sx={{ fontWeight: 600, color: "#01294E" }}>
            {isAlreadySetup ? t("myLanguageSelection.updateYourLanguage") : t("myLanguageSelection.setYourLanguages")}
          </Typography>
          <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
            {t("myLanguageSelection.updateLanguageDescription")}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Stack spacing={3}>
            {/* Native Languages */}
            <Box sx={{ px: 3 }}>
              <LanguageFieldLabel>
                {t("myLanguageSelection.nativeLanguages")}
                <Tooltip
                  title={isMobile ? "" : t("myLanguageSelection.nativeLanguagesTooltip")}
                  disableHoverListener={isMobile}
                >
                  <StyledInfoIcon onClick={() => handleInfoClick("native")} />
                </Tooltip>
              </LanguageFieldLabel>
              <Collapse in={showNativeInfo && isMobile}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t("myLanguageSelection.nativeLanguagesTooltip")}
                </Alert>
              </Collapse>
              <LanguageSelect
                multiple
                value={nativeLanguages}
                label="Select languages"
                hideLabel={nativeLanguages.length > 0}
                onChange={(e, val) => val && setNativeLanguages(val as Language[])}
                options={fullLanguageList}
              />
            </Box>
            <Divider />

            {/* Main Languages */}
            <Box sx={{ px: 3 }}>
              <LanguageFieldLabel>
                {t("myLanguageSelection.mainLanguages")}
                <Tooltip
                  title={isMobile ? "" : t("myLanguageSelection.mainLanguagesTooltip")}
                  disableHoverListener={isMobile}
                >
                  <StyledInfoIcon onClick={() => handleInfoClick("main")} />
                </Tooltip>
              </LanguageFieldLabel>
              <Collapse in={showMainInfo && isMobile}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t("myLanguageSelection.mainLanguagesTooltip")}
                </Alert>
              </Collapse>
              <LanguageSelect
                multiple
                value={mainLanguages}
                label="Select languages"
                hideLabel={mainLanguages.length > 0}
                onChange={(e, val) => val && setMainLanguages(val as Language[])}
                options={fullLanguageList}
              />
            </Box>
            <Divider />

            {/* Additional Languages */}
            <Box sx={{ px: 3 }}>
              <LanguageFieldLabel>
                {t("myLanguageSelection.additionalLanguages")}
                <Tooltip
                  title={isMobile ? "" : t("myLanguageSelection.additionalLanguagesTooltip")}
                  disableHoverListener={isMobile}
                >
                  <StyledInfoIcon onClick={() => handleInfoClick("additional")} />
                </Tooltip>
              </LanguageFieldLabel>
              <Collapse in={showAdditionalInfo && isMobile}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t("myLanguageSelection.additionalLanguagesTooltip")}
                </Alert>
              </Collapse>
              <LanguageSelect
                multiple
                value={additionalLanguages}
                label="Select languages"
                hideLabel={additionalLanguages.length > 0}
                onChange={(e, val) => val && setAdditionalLanguages(val as Language[])}
                options={fullLanguageList}
              />
            </Box>

            {/* Update Button */}
            <Box sx={{ p: 3 }}>
              <Button variant="contained" fullWidth onClick={handleUpdate}>
                {isAlreadySetup ? t("myLanguageSelection.update") : t("myLanguageSelection.continue")}
              </Button>
            </Box>
          </Stack>
        </DialogContent>
      </StyledDialog>

      {/* Success Modal */}
      <StyledDialog open={showSuccessModal} onClose={handleCloseSuccessModal} fullScreen={isMobile}>
        <DialogTitle
          sx={{ p: 3, mb: 2, gap: 1, display: "flex", flexDirection: "column", backgroundColor: palette.primary.light }}
        >
          <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ flex: 1, flexGrow: 0 }}>
            <IconButton
              onClick={handleCloseSuccessModal}
              sx={{
                color: "text.primary"
              }}
            >
              <CloseIcon fontSize="large" />
            </IconButton>
          </Stack>

          {/* Title */}
          <Typography variant="h5" component="div" sx={{ fontWeight: 600, color: "#01294E" }}>
            {t("myLanguageSelection.successTitle")}
          </Typography>

          {/* Description */}
          <Typography variant="body2" sx={{ color: palette.text.primary }}>
            {t("myLanguageSelection.successDescription")}{" "}
            <StyledLink to="/profile" onClick={handleCloseSuccessModal}>
              {t("myLanguageSelection.successDescriptionLink")}
            </StyledLink>
            {t("myLanguageSelection.successDescriptionEnd")}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={3}>
            {/* Language Sections */}
            {renderLanguageSection(nativeLanguages.map((l) => l.languageId), "myLanguageSelection.nativeLanguages")}
            {renderLanguageSection(mainLanguages.map((l) => l.languageId), "myLanguageSelection.mainLanguages")}
            {renderLanguageSection(additionalLanguages.map((l) => l.languageId), "myLanguageSelection.additionalLanguages")}

            {/* Start Exploring Button */}
            <Box sx={{ p: 3 }}>
              <Button variant="contained" fullWidth onClick={handleCloseSuccessModal}>
                {t("myLanguageSelection.startExploring")}
              </Button>
            </Box>
          </Stack>
        </DialogContent>
      </StyledDialog>
    </>
  )
}

export default UpdateLanguageDialog
